/**
 * Pure label-key helper for BranchingSwitcher.
 *
 * Lives in its own module so unit tests can import the helper without
 * pulling the React/Vite alias dependency graph in. The component
 * file re-exports `labelKeyFor` from here for backwards compat with
 * any consumer already deep-importing the function.
 */

export type SwitcherSurface = 'family' | 'school' | 'kitchen' | 'account' | 'billing'

/** The three call sites that share BranchingSwitcher. Kept here (not
 *  imported from the .tsx) so this module stays React-free for tests. */
export type SwitcherKind = 'orders' | 'billing' | 'settings'

export type SwitcherLabelKey =
  | 'switcher.personal'
  | 'switcher.work'
  | 'switcher.family'
  | 'switcher.school'
  | 'switcher.kitchen'
  | 'switcher.account'
  | 'switcher.profile'
  | 'switcher.billing'

/**
 * Map a (variant count, surface) tuple to the canonical translation
 * key for that button's label. The "Personal / Work" framing per the
 * May 2026 nav doc shows up only when the user has two variants — the
 * common multi-hat parent+admin case. ORDO_ADMIN with all three
 * variants gets literal Family / School / Kitchen labels because the
 * Personal/Work framing breaks down (which hat is "Personal" when you
 * hold all three?).
 */
export function labelKeyFor(surface: SwitcherSurface, variantCount: number, kind?: SwitcherKind): SwitcherLabelKey {
  // Settings uses literal per-surface labels regardless of variant count:
  // My School → "School", My Kitchen → "Kitchen", Account → "Profile".
  // The Personal/Work framing (below) is for Orders/Billing only.
  if (kind === 'settings') {
    if (surface === 'account') {
      return 'switcher.profile'
    }
    if (surface === 'billing') {
      return 'switcher.billing'
    }
    if (surface === 'kitchen') {
      return 'switcher.kitchen'
    }
    if (surface === 'family') {
      return 'switcher.family'
    }
    return 'switcher.school'
  }
  if (surface === 'billing') {
    return 'switcher.billing'
  }
  if (variantCount >= 3) {
    if (surface === 'family') {
      return 'switcher.family'
    }
    if (surface === 'school') {
      return 'switcher.school'
    }
    if (surface === 'account') {
      return 'switcher.account'
    }
    return 'switcher.kitchen'
  }
  // 2-variant mode: family|account → Personal, school|kitchen → Work.
  return surface === 'family' || surface === 'account' ? 'switcher.personal' : 'switcher.work'
}
